import React from 'react';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'coral' | 'outline' | 'ghost' | 'white';
    size?: 'sm' | 'md' | 'lg' | 'xl';
    fullWidth?: boolean;
    isLoading?: boolean;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    className?: string;
    as?: any;
    to?: string;
    href?: string;
}

const Button: React.FC<ButtonProps> = ({
    children,
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    isLoading = false,
    leftIcon,
    rightIcon,
    className = '',
    as,
    ...props
}) => {
    const baseStyles = 'inline-flex items-center justify-center font-bold transition-all duration-300 font-secondary rounded-lg active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2';

    const variants = {
        primary: 'bg-navy text-white hover:bg-navy-dark focus:ring-navy shadow-lg hover:shadow-xl hover:-translate-y-0.5',
        secondary: 'bg-mint text-navy hover:bg-mint-dark focus:ring-mint shadow-md hover:shadow-lg hover:-translate-y-0.5',
        coral: 'bg-coral text-white hover:bg-coral-dark focus:ring-coral shadow-lg hover:shadow-xl hover:-translate-y-0.5',
        outline: 'border-2 border-navy text-navy hover:bg-navy hover:text-white focus:ring-navy',
        ghost: 'bg-transparent text-navy hover:bg-gray-100 focus:ring-gray-200',
        white: 'bg-white text-navy hover:bg-gray-50 focus:ring-white shadow-lg hover:shadow-xl hover:-translate-y-0.5'
    };

    const sizes = {
        sm: 'px-4 py-2 text-sm',
        md: 'px-6 py-3 text-base',
        lg: 'px-8 py-4 text-lg',
        xl: 'px-10 py-5 text-xl'
    };

    const width = fullWidth ? 'w-full' : '';

    const Component = as || 'button';

    return (
        <Component
            className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${width} ${className}`}
            disabled={isLoading}
            {...props}
        >
            {isLoading && (
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            )}
            {!isLoading && leftIcon && <span className="mr-2">{leftIcon}</span>}
            {children}
            {!isLoading && rightIcon && <span className="ml-2">{rightIcon}</span>}
        </Component>
    );
};

export default Button;
